'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, AlertTriangle } from 'lucide-react';

interface ToastProps {
  message: string;
  linkText?: string;
  linkHref?: string;
  autoDismissMs?: number;
}

export default function ToastNotification({
  message,
  linkText,
  linkHref,
  autoDismissMs = 8000,
}: ToastProps) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (visible && autoDismissMs > 0) {
      const timer = setTimeout(() => setVisible(false), autoDismissMs);
      return () => clearTimeout(timer);
    }
  }, [visible, autoDismissMs]);

  if (dismissed || !visible) return null;

  return (
    <div className="fixed bottom-16 left-4 z-50 max-w-sm w-full bg-foreground text-white rounded-lg shadow-modal border-l-4 border-primary p-4 transition-all duration-300">
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-600 text-white leading-snug">{message}</p>
          {linkText && linkHref && (
            <Link
              href={linkHref}
              className="mt-1 text-xs text-primary hover:text-red-400 transition-colors font-600"
            >
              {linkText} →
            </Link>
          )}
        </div>
        <button
          onClick={() => {
            setVisible(false);
            setDismissed(true);
          }}
          aria-label="Dismiss notification"
          className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}